import { useRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/hooks/useRelatorioAM";
import { RelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/model/RelatorioAM";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import { SaveOutlined, UploadOutlined } from "@ant-design/icons";
import {
  App,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Upload,
  UploadFile,
} from "antd";
import { RcFile } from "antd/lib/upload";
import { Dayjs } from "dayjs";
import { useState } from "react";

interface FormPlanoAcaoRAMProps {
  item: RelatorioAM["itens"][number];
  relatorio: RelatorioAM | undefined;
  refetchRelatorio: () => Promise<any>;
}

interface FormValues {
  solucaoGeral: string;
  prazoAcaoImediata: Dayjs;
  medidaCorretiva: string;
  prazoAcaoCorretiva: Dayjs;
  medidaPreventiva: string;
  prazoAcaoPreventiva: Dayjs;
  anexos: UploadFile<any>[];
}

interface PlanoAcaoRequestRelatorioAM {
  ramItem: {
    id: number;
    uuid: string;
    secaoId: number;
    perguntaId: number;
    dataResposta: string; //2025-01-01T10:00:00
  } | null;
  solucaoGeral: string;
  prazoAcaoImediata: string; //2025-01-01T10:00:00
  medidaCorretiva: string;
  prazoAcaoCorretiva: string; //2025-01-01T10:00:00
  medidaPreventiva: string;
  prazoAcaoPreventiva: string; //2025-01-01T10:00:00
}

export function FormPlanoAcaoRAM({
  item,
  relatorio,
  refetchRelatorio,
}: FormPlanoAcaoRAMProps) {
  const [form] = Form.useForm<FormValues>();
  const { submitPlanoAcaoRelatorioAMApi } = useRelatorioAM();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { message, notification } = App.useApp();

  const dummyRequest = async ({ file, onSuccess }: any) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const onPreview = async (file: UploadFile) => {
    let src = file.url as string;
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj as RcFile);
        reader.onload = () => resolve(reader.result as string);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  };

  const criarArquivoComNovoNome = (arquivo: RcFile, novoNome: string): File => {
    return new File([arquivo], novoNome, {
      type: arquivo.type,
      lastModified: arquivo.lastModified,
    });
  };

  const handleSubmit = async (values: FormValues) => {
    if (!relatorio) return;

    try {
      setIsSubmitting(true);
      message.loading({
        content: "Salvando plano de ação...",
        key: "salvar-plano-acao",
        duration: 0,
      });

      const formData = new FormData();

      const request: PlanoAcaoRequestRelatorioAM = {
        ramItem: {
          id: item.id!,
          uuid: item.uuid,
          secaoId: item.secao!.id,
          perguntaId: item.pergunta!.id,
          dataResposta: item.dataResposta,
        },
        solucaoGeral: values.solucaoGeral,
        prazoAcaoImediata: values.prazoAcaoImediata.format(
          "YYYY-MM-DDTHH:mm:ss"
        ),
        medidaCorretiva: values.medidaCorretiva,
        prazoAcaoCorretiva: values.prazoAcaoCorretiva.format(
          "YYYY-MM-DDTHH:mm:ss"
        ),
        medidaPreventiva: values.medidaPreventiva,
        prazoAcaoPreventiva: values.prazoAcaoPreventiva.format(
          "YYYY-MM-DDTHH:mm:ss"
        ),
      };

      formData.append(
        "planoAcaoRequest",
        new Blob([JSON.stringify(request)], {
          type: "application/json",
        })
      );

      values.anexos?.forEach((file) => {
        if (file.originFileObj) {
          const novoNome = `${item.uuid}_${file.name}`;
          const arquivoComNovoNome = criarArquivoComNovoNome(
            file.originFileObj,
            novoNome
          );
          formData.append("anexos", arquivoComNovoNome);
        }
      });

      await submitPlanoAcaoRelatorioAMApi(relatorio.id!, formData);

      message.destroy("salvar-plano-acao");
      setIsSubmitting(false);
      notification.success({
        message: "Plano de ação salvos com sucesso na base de dados.",
        description: "Plano de ação será removido da lista de pendentes.",
      });

      refetchRelatorio();
    } catch (error) {
      console.error("Erro ao enviar plano de ação:", error);
      setIsSubmitting(false);
      message.destroy("salvar-plano-acao");
      const messageError = formatMessageError(error);
      notification.error({
        message: "Não foi possível salvar o plano de ação",
        description: messageError,
      });
    }
  };

  return (
    <>
      <Form
        id="form-plano-acao"
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        onFinishFailed={() => {
          message.error("Preencha os campos obrigatórios.");
        }}
      >
        {" "}
        <Row gutter={[8, 8]}>
          <Col xs={24}>
            <Form.Item
              name={"solucaoGeral"}
              label="Solução Geral"
              rules={[
                {
                  required: true,
                  message: "A solução geral é obrigatória.",
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <Input.TextArea
                rows={2}
                placeholder="Descreva a solução geral para corrigir esta não conformidade"
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name={"prazoAcaoImediata"}
              label="Prazo Ação Imediata"
              rules={[
                {
                  required: true,
                  message: "O prazo da ação imediata é obrigatório.",
                },
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione o prazo"
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name={"medidaCorretiva"}
              label="Medida Corretiva"
              rules={[
                {
                  required: true,
                  message: "A medida corretiva é obrigatória.",
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <Input.TextArea
                rows={2}
                placeholder="Descreva a medida corretiva para corrigir esta não conformidade"
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name={"prazoAcaoCorretiva"}
              label="Prazo Ação Corretiva"
              rules={[
                {
                  required: true,
                  message: "O prazo da medida corretiva é obrigatório.",
                },
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione o prazo"
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name={"medidaPreventiva"}
              label="Medida Preventiva"
              rules={[
                {
                  required: true,
                  message: "A medida preventiva é obrigatória.",
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <Input.TextArea
                rows={2}
                placeholder="Descreva a medida preventiva para prevenir esta não conformidade"
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name={"prazoAcaoPreventiva"}
              label="Prazo Medida Preventiva"
              rules={[
                {
                  required: true,
                  message: "O prazo da medida preventiva é obrigatório.",
                },
              ]}
            >
              <DatePicker
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Selecione o prazo"
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label="Anexos"
              name={"anexos"}
              valuePropName="fileList"
              getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
            >
              <Upload
                name="files"
                multiple
                listType="picture"
                beforeUpload={() => false}
                maxCount={10}
                onPreview={onPreview}
                customRequest={dummyRequest}
              >
                <Button style={{ width: 200 }} icon={<UploadOutlined />}>
                  Anexar Documentos
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
        <Row justify="center">
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              icon={<SaveOutlined />}
            >
              Salvar
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}
